import { conf } from '../config/config';
import { DEMO_MODE } from '../util/Constants';
import { getLocalStore } from '../util/Helpers';
import { tempAccountDashboard, tempCampaignStudio, tempGirls, tempCampaigns } from '../demo/demo';

const apiEndpoint = (path) => `${conf.endpoint.replace(/\/$/, '')}${path}`;
const slugify = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
const normalizedStatus = (value) => String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
const isActiveModel = (model) => {
    const status = model?.status ?? model?.modelStatus ?? model?.data?.status;
    return !status || normalizedStatus(status) === 'active';
};
const campaignForModel = (model) => {
    if (Array.isArray(model?.campaigns)) return model.campaigns;
    return model?.activeCampaign || model?.currentCampaign || model?.campaign || model?.advertisingCampaign || model?.advertisementCampaign || null;
};
export const isPremiumActive = (source) => source?.premiumActive === true;
const hasCampaignData = (model) => ['campaigns', 'activeCampaign', 'currentCampaign', 'campaign', 'advertisingCampaign', 'advertisementCampaign']
    .some((key) => Object.prototype.hasOwnProperty.call(model || {}, key));
const isPublicCampaign = (campaign) => {
    if (!['auto', 'active'].includes(normalizedStatus(campaign?.status))) return false;
    const expiresAt = campaign?.data?.expiresAt || campaign?.expiresAt;
    if (!expiresAt) return true;
    const expiryTime = new Date(expiresAt).getTime();
    return Number.isFinite(expiryTime) && expiryTime > Date.now();
};
const hasActivePublicCampaign = (model) => {
    const campaign = campaignForModel(model);
    if (!hasCampaignData(model)) return true;
    if (Array.isArray(campaign)) return campaign.some(isPublicCampaign);
    return isPublicCampaign(campaign);
};
const isPublicModel = (model) => isActiveModel(model) && hasActivePublicCampaign(model);
const hasPublicCampaign = (model) => {
    const modelSlug = slugify(model?.slug || model?.name);
    return tempCampaigns.some((campaign) => {
        const campaignModelSlug = slugify(campaign?.modelSlug || campaign?.model || campaign?.slug || campaign?.name);
        return (campaignModelSlug === modelSlug || campaignModelSlug.startsWith(`${modelSlug}-`)) && isPublicCampaign(campaign);
    });
};

export const modelProfilePath = (city, model) => {
    const citySlug = slugify(typeof city === 'object' ? city.slug || city.name : city);
    const modelSlug = slugify(typeof model === 'object' ? model.slug || model.name : model);
    return `/models/${encodeURIComponent(citySlug)}/${encodeURIComponent(modelSlug)}`;
};

const demoModels = () => Object.entries(tempGirls).flatMap(([citySlug, city]) => city.talent.map((model) => ({
    ...model,
    status: model.status || 'active',
    slug: slugify(model.slug || model.name),
    city: city.title,
    citySlug,
    photos: Array.isArray(model.photos) ? model.photos : [],
    description: model.description || tempCampaignStudio.description,
    parameters: model.parameters || {...tempCampaignStudio.parameters},
    cityRates: model.cityRates || tempCampaignStudio.cityRates.map((rate) => ({...rate})),
    travelRates: model.travelRates || tempCampaignStudio.travelRates.map((rate) => ({...rate})),
    tourSchedule: model.tourSchedule || tempCampaignStudio.tourSchedule.map((tour) => ({...tour})),
    contacts: model.contacts || {...tempAccountDashboard.model.contacts}
})));

const parseResponse = async (response, fallbackMessage) => {
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.error?.message || fallbackMessage);
    return result.data;
};

export async function getPublicModels(citySlug = '') {
    if (getLocalStore(DEMO_MODE, false, true)) {
        const models = demoModels().filter((model) => isPublicModel(model) && hasPublicCampaign(model));
        return citySlug ? models.filter((model) => model.citySlug === slugify(citySlug)) : models;
    }

    const queryParams = new URLSearchParams();
    if (citySlug) queryParams.set('city', slugify(citySlug));
    const query = queryParams.toString();
    const data = await parseResponse(await fetch(apiEndpoint(`/api/models${query ? `?${query}` : ''}`)), 'We could not load the model profiles. Please try again.');
    return Array.isArray(data) ? data.filter(isPublicModel) : [];
}

export async function getPublicModel(citySlug, modelSlug) {
    if (getLocalStore(DEMO_MODE, false, true)) {
        const model = demoModels().find((item) => item.citySlug === slugify(citySlug) && item.slug === slugify(modelSlug) && isPublicModel(item) && hasPublicCampaign(item));
        if (!model) throw new Error('Model not found.');
        return model;
    }

    const model = await parseResponse(
        await fetch(apiEndpoint(`/api/models/${encodeURIComponent(slugify(citySlug))}/${encodeURIComponent(slugify(modelSlug))}`)),
        'We could not load this model profile. Please try again.'
    );
    if (!isPublicModel(model)) throw new Error('Model not found.');
    return model;
}
